import { __assign } from "tslib";
import React, { useRef } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { Popper } from "react-popper";
import { getOverlayRoot } from "../_util/get-overlay-root";
import { mergeStyle } from "../_util/merge-style";
import { callBoth } from "../_util/call-both";
import { ScaleTransition } from "../transition";
import { useVisibleTransition } from "../_util/use-visible-transition";
import { injectValue } from "../_util/inject-value";
import { useConfig } from "../_util/config-context";
import { noop } from "../_util/noop";
import { mergeRefs } from "../util";
var scaleOriginForPlacement = (function (originMap) { return function (placement) {
    var basePlacement = placement.split("-").shift();
    return originMap[basePlacement];
}; })({
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
});
/**
 * 为定位元素创建一个覆盖层
 *
 * @example
 *
  ```js
  const [visible, setVisible] = useState(false);
  const open = () => setVisible(true);
  const close = () => setVisible(false);
  <Overlay
    visible={visible}
    content={<div>我是浮层内容，<a onClick={close}>关闭</a></div>}
    children={ref => <a ref={ref} onClick={open}>点击弹出浮层</a>}
  />
  ```
 */
export var OverlayLayer = React.forwardRef(function OverlayLayer(_a, ref) {
    var content = _a.content, overlayRef = _a.overlayRef, _b = _a.overlayProps, overlayProps = _b === void 0 ? {} : _b, _c = _a.placement, placement = _c === void 0 ? "bottom-start" : _c, visible = _a.visible, _d = _a.placementOffset, placementOffset = _d === void 0 ? 5 : _d, _e = _a.transitionTimeout, transitionTimeout = _e === void 0 ? { enter: 50, exit: 300 } : _e, _f = _a.destroyOnClose, destroyOnClose = _f === void 0 ? true : _f, updateOnDimensionChange = _a.updateOnDimensionChange, referenceElement = _a.referenceElement, _g = _a.animationScaleFrom, animationScaleFrom = _g === void 0 ? 0.94 : _g, // 为什么？因为此时效果比较好
    escapeWithReference = _a.escapeWithReference, popupContainer = _a.popupContainer, _h = _a.modifiers, modifiers = _h === void 0 ? {} : _h, _j = _a.flipped, flipped = _j === void 0 ? true : _j, _k = _a.positionFixed, positionFixed = _k === void 0 ? true : _k, _l = _a.onExited, onExited = _l === void 0 ? noop : _l;
    var _m = useConfig(), classPrefix = _m.classPrefix, globalPopupContainer = _m.popupContainer;
    var firstRender = useRef(!!visible);
    if (visible && !firstRender.current) {
        firstRender.current = true;
    }
    // visible 启动时，才开始渲染内容，进行动画
    var _o = useVisibleTransition(visible), contentIn = _o.contentIn, shouldContentRender = _o.shouldContentRender, shouldContentEnter = _o.shouldContentEnter, onContentExit = _o.onContentExit;
    if (!shouldContentRender &&
        (destroyOnClose || (!destroyOnClose && !firstRender.current))) {
        return null;
    }
    // 渲染定位组件
    return ReactDOM.createPortal(React.createElement(Popper, { referenceElement: referenceElement, placement: placement, modifiers: __assign({ offset: {
                enabled: true,
                offset: Array.isArray(placementOffset)
                    ? placementOffset[0] + ", " + placementOffset[1]
                    : "0," + placementOffset,
            }, preventOverflow: {
                escapeWithReference: escapeWithReference,
                boundariesElement: "viewport",
            }, computeStyle: {
                // 使用 translate3d() 在 Windows 下有 DPI 缩放时会导致模糊
                // 只有在 resize 和 scroll 的时候才会有更新的需求，暂时关闭 GPU 加速
                gpuAcceleration: false,
            }, flip: { enabled: flipped }, hide: { enabled: flipped } }, modifiers), eventsEnabled: updateOnDimensionChange, positionFixed: positionFixed }, function (popper) {
        var overlayContent = injectValue(content)(__assign(__assign({}, popper), { visible: visible }));
        return (React.createElement(ScaleTransition, { from: animationScaleFrom, origin: scaleOriginForPlacement(placement), timeout: transitionTimeout, in: shouldContentEnter, onEnter: function () { return popper.scheduleUpdate(); }, onExited: callBoth(onContentExit, onExited), unmountOnExit: destroyOnClose },
            React.createElement("div", __assign({}, overlayProps, { 
                // 覆盖层类名，样式表中包含 z-index 来确定覆盖层的层级
                className: classNames(classPrefix + "-overlay", overlayProps.className), 
                // ref 同时提供给 popper 和 contentRef
                ref: mergeRefs(popper.ref, overlayRef, ref), 
                // popper.style 为 overlay 浮层提供定位
                // contentStyle 提供给用户改写的可能
                style: mergeStyle(popper.style, {
                    willChange: null,
                    display: !destroyOnClose && !contentIn ? "none" : undefined,
                }, overlayProps.style), "data-placement": popper.placement || placement }), React.isValidElement(overlayContent) ? (overlayContent) : (React.createElement("span", null, overlayContent)))));
    }), getOverlayRoot(popupContainer || globalPopupContainer));
});
OverlayLayer.displayName = "OverlayLayer";
//# sourceMappingURL=OverlayLayer.js.map